import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LEDGER_TYPE_MAP } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date, money } from '../../lib/format';
import {
  FX_REVALUATION_LABEL,
  STATEMENT_TITLES,
  flattenLines,
  fxExtra,
  fxText,
  statementsDoc,
  type BalanceSheetData,
  type FinancialStatement,
  type IncomeStatementData,
  type StatementLine,
  type TrialBalanceData,
} from '../../lib/financialStatements';
import { Icon } from '../../components/Icon';
import { ExportMenu } from '../../components/ExportMenu';
import { Badge, ErrorState, PageHead, Stat } from '../../components/ui';

type Tab = FinancialStatement;

/** التاريخ بالتوقيت المحلي كما يكتبه حقل التاريخ (toISOString يرجع يومًا في مصر قبل الثانية صباحًا). */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** أول يناير من السنة الجارية — القوائم المالية تُقرأ عادةً من أول السنة المالية. */
const yearStart = () => `${new Date().getFullYear()}-01-01`;

export function FinancialReports() {
  const [tab, setTab] = useState<Tab>('income');
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(ymd(new Date()));

  const range = { from: new Date(from).toISOString(), to: new Date(to).toISOString() };

  /*
   * ميزان المراجعة تراكمي حتى تاريخ، لا حركة مدة.
   *
   * قصره على شهر يعرض ما تحرّك فيه فقط، فيظهر حساب البنك برصيد سالب لأن
   * المصروف في ذلك الشهر تجاوز الوارد فيه — وهو رقم لا معنى له خارج سياقه.
   * الميزان يُراجَع كما في تاريخ: أرصدة الحسابات مجتمعة منذ البداية.
   */
  const trial = useQuery({
    queryKey: ['trial-balance', to],
    queryFn: () =>
      api<TrialBalanceData>(`/accounting/reports/trial-balance${qs({ to: new Date(to).toISOString() })}`),
    enabled: tab === 'trial',
  });

  const income = useQuery({
    queryKey: ['income-statement', from, to],
    queryFn: () =>
      api<IncomeStatementData>(`/accounting/reports/income-statement${qs(range)}`),
    enabled: tab === 'income',
  });

  const balanceSheet = useQuery({
    queryKey: ['balance-sheet', to],
    queryFn: () =>
      api<BalanceSheetData>(`/accounting/reports/balance-sheet${qs({ asOf: new Date(to).toISOString() })}`),
    enabled: tab === 'balance',
  });

  /* عمود العملة الأجنبية في الميزان لمن عنده حسابات بعملات أجنبية فقط. */
  const trialFx = !!trial.data?.rows.some((r) => r.fx);

  /* ما حُمّل لهذه المدة يُعاد استعماله في التصدير بدل جلبه مرّةً ثانية. */
  const loaded = { income: income.data, balance: balanceSheet.data, trial: trial.data };

  return (
    <div className="page">
      <PageHead
        title="القوائم المالية"
        subtitle={
          tab === 'income'
            ? 'قائمة الدخل عن المدة المختارة — من القيود المرحَّلة وحدها'
            : tab === 'balance'
              ? 'الميزانية كما في التاريخ المختار — من القيود المرحَّلة وحدها'
              : 'ميزان المراجعة كما في التاريخ المختار — أرصدة تراكمية'
        }
        actions={
          <div className="row">
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="من تاريخ"
              style={{ width: 'auto' }}
              disabled={tab === 'balance' || tab === 'trial'}
            />
            <span className="mute small">إلى</span>
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="إلى تاريخ"
              style={{ width: 'auto' }}
            />
            {/*
              التصدير من البيانات المعروضة إن كانت محمَّلة، وإلّا تُجلب —
              فالقوائم الثلاث معًا تعمل من أيّ تبويب.
            */}
            <ExportMenu
              scopes={[
                {
                  key: 'current',
                  label: STATEMENT_TITLES[tab],
                  build: () => statementsDoc(tab, from, to, loaded),
                },
                {
                  key: 'all',
                  label: 'القوائم الثلاث',
                  hint: 'إكسل: ورقةٌ لكل قائمة — PDF: صفحةٌ لكل قائمة',
                  build: () => statementsDoc('all', from, to, loaded),
                },
              ]}
            />
          </div>
        }
      />

      <div className="tabs" role="tablist">
        <button className="tab" role="tab" aria-selected={tab === 'income'} onClick={() => setTab('income')}>
          قائمة الدخل
        </button>
        <button className="tab" role="tab" aria-selected={tab === 'balance'} onClick={() => setTab('balance')}>
          الميزانية
        </button>
        <button className="tab" role="tab" aria-selected={tab === 'trial'} onClick={() => setTab('trial')}>
          ميزان المراجعة
        </button>
      </div>

      {/* ---- قائمة الدخل ---- */}
      {tab === 'income' && (
        <>
          {income.isError && <ErrorState error={income.error} onRetry={() => void income.refetch()} />}

          <div className="stats">
            <Stat label="الإيرادات" value={<span className="num">{money(income.data?.revenue.total ?? 0)}</span>} />
            <Stat
              label="مجمل الربح"
              value={<span className="num">{money(income.data?.grossProfit ?? 0)}</span>}
              note="بعد التكلفة المباشرة"
            />
            <Stat label="المصروفات" value={<span className="num">{money(income.data?.expenses.total ?? 0)}</span>} />
            <Stat
              label="صافي الربح"
              value={
                <span
                  className="num"
                  style={{
                    color: (income.data?.netProfit ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)',
                  }}
                >
                  {money(income.data?.netProfit ?? 0)}
                </span>
              }
              note={income.data?.margin !== null && income.data?.margin !== undefined
                ? `هامش ${income.data.margin}٪`
                : undefined}
            />
          </div>

          <div className="two-col">
            <StatementCard title="الإيرادات" lines={income.data?.revenue.lines ?? []} total={income.data?.revenue.total ?? 0} loading={income.isPending} />
            <StatementCard title="المصروفات" lines={income.data?.expenses.lines ?? []} total={income.data?.expenses.total ?? 0} loading={income.isPending} />
          </div>

          {/*
            فروق تقييم العملة سطر مستقل بعد صافي الربح الدفتري: تغيّر قيمة
            الأرصدة بالعملات الأجنبية بسعر الصرف، لا ربح تشغيل — ولا قيد لها.
          */}
          {!!income.data?.fxRevaluation && (
            <div className="card">
              <div className="table-wrap" style={{ minHeight: 0 }}>
                <table className="table">
                  <tbody>
                    <tr>
                      <td>صافي الربح</td>
                      <td className="num end" style={{ width: 160 }}>{money(income.data.netProfit)}</td>
                    </tr>
                    <tr>
                      <td>
                        {FX_REVALUATION_LABEL}
                        <div className="tiny mute">
                          تغيّر قيمة الأرصدة بالعملات الأجنبية بين أول المدة وآخرها بسعر الصرف — غير مرحَّلة في الدفاتر.
                        </div>
                      </td>
                      <td className={`num end ${income.data.fxRevaluation < 0 ? 'neg' : 'pos'}`}>
                        {money(income.data.fxRevaluation)}
                      </td>
                    </tr>
                    <tr>
                      <td className="strong">صافي الربح بعد فروق التقييم</td>
                      <td className="num end strong">
                        {money(income.data.netProfitAfterFx ?? income.data.netProfit)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- الميزانية ---- */}
      {tab === 'balance' && (
        <>
          {balanceSheet.isError && (
            <ErrorState error={balanceSheet.error} onRetry={() => void balanceSheet.refetch()} />
          )}

          {/*
            حالة التوازن أول ما يُعرض.
            ميزانية غير متوازنة تعني خللًا في الترحيل، وقراءة أرقامها قبل
            معرفة ذلك مضيعة للوقت.
          */}
          {balanceSheet.data && (
            <div
              className={`alert ${balanceSheet.data.balanced ? 'alert-info' : 'alert-danger'} mb-4`}
            >
              <Icon name={balanceSheet.data.balanced ? 'check' : 'alert'} size={18} />
              <div>
                {balanceSheet.data.balanced ? (
                  <>
                    الميزانية متوازنة حتى {date(to)}: الأصول تساوي الالتزامات وحقوق الملكية.
                  </>
                ) : (
                  <>
                    الميزانية غير متوازنة بفرق{' '}
                    <span className="num strong">{money(Math.abs(balanceSheet.data.difference))}</span>.
                    راجع القيود المرحَّلة.
                  </>
                )}
              </div>
            </div>
          )}

          <div className="two-col">
            <StatementCard
              title="الأصول"
              lines={balanceSheet.data?.assets.lines ?? []}
              total={balanceSheet.data?.assets.total ?? 0}
              loading={balanceSheet.isPending}
            />
            <div>
              <StatementCard
                title="الالتزامات"
                lines={balanceSheet.data?.liabilities.lines ?? []}
                total={balanceSheet.data?.liabilities.total ?? 0}
                loading={balanceSheet.isPending}
              />
              <StatementCard
                title="حقوق الملكية"
                lines={balanceSheet.data?.equity.lines ?? []}
                total={balanceSheet.data?.equity.total ?? 0}
                loading={balanceSheet.isPending}
                extra={
                  balanceSheet.data
                    ? [
                        { label: 'صافي ربح المدة', amount: balanceSheet.data.netProfit },
                        // فرق تقييم الأرصدة الأجنبية بسعر تاريخ الميزانية — يحفظ توازنها.
                        ...fxExtra(balanceSheet.data.fxRevaluation),
                      ]
                    : []
                }
              />
            </div>
          </div>
        </>
      )}

      {/* ---- ميزان المراجعة ---- */}
      {tab === 'trial' && (
        <>
          {trial.isError && <ErrorState error={trial.error} onRetry={() => void trial.refetch()} />}

          {trial.data && (
            <div className={`alert ${trial.data.totals.balanced ? 'alert-info' : 'alert-danger'} mb-4`}>
              <Icon name={trial.data.totals.balanced ? 'check' : 'alert'} size={18} />
              <div>
                {trial.data.totals.balanced
                  ? 'الميزان متوازن: مجموع الأرصدة المدينة يساوي الدائنة.'
                  : 'الميزان غير متوازن — خلل في الترحيل يحتاج مراجعة.'}
              </div>
            </div>
          )}

          <div className="card">
            <div className="table-wrap sticky-head">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>الكود</th>
                    <th>الحساب</th>
                    <th style={{ width: 110 }}>النوع</th>
                    <th style={{ width: 140 }}>مدين</th>
                    <th style={{ width: 140 }}>دائن</th>
                    <th style={{ width: 140 }}>الرصيد</th>
                    {trialFx && <th style={{ width: 190 }}>بالعملة الأجنبية</th>}
                  </tr>
                </thead>
                <tbody>
                  {(trial.data?.rows ?? []).map((row) => (
                    <tr key={row.id}>
                      <td className="num cell-mute">{row.code}</td>
                      <td className="cell-strong">{row.name}</td>
                      <td>
                        <Badge kind={LEDGER_TYPE_MAP[row.type]?.kind}>
                          {LEDGER_TYPE_MAP[row.type]?.label}
                        </Badge>
                      </td>
                      <td className="num">{row.debit ? money(row.debit) : '—'}</td>
                      <td className="num">{row.credit ? money(row.credit) : '—'}</td>
                      <td className="num strong">{money(row.balance)}</td>
                      {trialFx && (
                        <td className="num cell-mute">
                          {row.fx ? (
                            <>
                              {fxText(row.fx)}
                              {row.fx.value !== null && (
                                <div className="tiny">≈ {money(row.fx.value)} بسعر اليوم</div>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                {trial.data && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--line)' }}>
                      <td colSpan={3} className="strong">
                        الإجمالي
                      </td>
                      <td className="num strong">{money(trial.data.totals.debit)}</td>
                      <td className="num strong">{money(trial.data.totals.credit)}</td>
                      <td />
                      {trialFx && <td />}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {trial.data?.rows.length === 0 && (
              <p className="mute small center" style={{ padding: 'var(--sp-6)' }}>
                لا توجد قيود مرحَّلة في هذه المدة.
              </p>
            )}
          </div>
        </>
      )}

    </div>
  );
}

/* ==========================================================================
   بطاقة قائمة مالية
   ========================================================================== */

function StatementCard({
  title,
  lines,
  total,
  loading,
  extra = [],
}: {
  title: string;
  lines: StatementLine[];
  total: number;
  loading: boolean;
  extra?: Array<{ label: string; amount: number }>;
}) {
  const rows = flattenLines(lines);

  const grandTotal = total + extra.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">{title}</h2>
        <span className="num strong" style={{ marginInlineStart: 'auto' }}>
          {money(grandTotal)}
        </span>
      </div>

      {loading ? (
        <div className="card-body">
          <div className="skeleton" style={{ height: 140 }} />
        </div>
      ) : rows.length === 0 && extra.length === 0 ? (
        <div className="card-body">
          <p className="mute small">لا توجد حركة في هذه المدة.</p>
        </div>
      ) : (
        /* رأسٌ ثابت: اسما العمودين ظاهران مهما طالت الشجرة. والحدُّ الأدنى
           للارتفاع يُلغى هنا — قسمٌ بحسابين لا يحتاج فراغًا تحته. */
        <div className="table-wrap sticky-head" style={{ minHeight: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>الحساب</th>
                <th className="end" style={{ width: 140 }}>
                  المبلغ
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span style={{ paddingInlineStart: row.level * 16 }}>
                      <span className="num cell-mute">{row.code}</span>{' '}
                      <span className={row.isGroup ? 'strong' : undefined}>{row.name}</span>
                    </span>
                  </td>
                  <td className="num end" style={{ width: 140 }}>
                    <span className={row.isGroup ? 'strong' : undefined}>{money(row.amount)}</span>
                    {/* الحساب بعملة أجنبية: المبلغ أعلاه بالجنيه بسعر القائمة، وبعملته هنا. */}
                    {row.fx && <div className="tiny mute">{fxText(row.fx)}</div>}
                  </td>
                </tr>
              ))}
              {extra.map((e) => (
                <tr key={e.label}>
                  <td className="strong">{e.label}</td>
                  <td className="num end strong">{money(e.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
